import { getLocalDataIfNotExpired, getLocalVariable, setLocalVariable } from '@services/storage';

import { DeliveryModeMapped } from '@app-types/delivery';
import { FaiProduct } from '@app-types/fai';
import { CartType, Connectivity, JOURNEYS, LightOptionType, OptionType, PlanType } from '@app-types/product';
import { Consent } from '@app-types/user';

import { v4 as uuid } from 'uuid';
import { create } from 'zustand';

type cartStoreType = {
    selectedPlan: PlanType | null;
    setSelectedPlan: (p: PlanType | null) => void;
    selectedOption: LightOptionType | null;
    setSelectedOption: (option: LightOptionType | null) => void;
    selectedFaiProduct: FaiProduct | null;
    setSelectedFaiProduct: (faiProduct: FaiProduct | null) => void;
    byouCart: null | CartType;
    addToCart: ({ product, options }: Readonly<{ product: PlanType; options: OptionType[] }>) => void;
    updateCart: (cartItems: Partial<CartType>) => void;
    updateCartOptions: (option: OptionType) => void;
    addDeliveryMode: (deliveryMode: DeliveryModeMapped) => void;
    removeDeliveryMode: () => void;
    selectedFaiOption: string | null;
    setSelectedFaiOption: (optionId: string) => void;
    temporarilySelectedPlan: PlanType | null;
    setTemporarilySelectedPlan: (plan: PlanType | null) => void;
    selectedNetwork: Connectivity;
    setSelectedNetwork: (network: Connectivity) => void;
    temporarilySelectedFaiProduct: FaiProduct | null;
    setTemporarilySelectedFaiProduct: (faiProduct: FaiProduct | null) => void;
    otisConfigurator: { value: JOURNEYS | undefined };
    setOtisConfigurator: (newJourney: JOURNEYS) => void;
    switchJourney: (journey: JOURNEYS) => void;
    consent: Consent | null;
    setConsent: (consentData: Consent) => void;
};
const CART_STORAGE_KEY = 'cart';
const CONSENT_KEY = 'consent';

type JourneyResets = Partial<
    Pick<
        cartStoreType,
        | 'selectedPlan'
        | 'temporarilySelectedPlan'
        | 'selectedFaiProduct'
        | 'temporarilySelectedFaiProduct'
        | 'selectedFaiOption'
    >
>;

/**
 * Selections that must be dropped when leaving a journey.
 * FAI keeps its box selection, every other journey drops it.
 * BYOU and PACKS serve disjoint catalogs, so the mobile plan is dropped as well.
 */
const getJourneyResets = (journey: JOURNEYS): JourneyResets => {
    const clearedFai = {
        selectedFaiProduct: null,
        temporarilySelectedFaiProduct: null,
        selectedFaiOption: '',
    };
    const clearedPlan = { selectedPlan: null, temporarilySelectedPlan: null };

    return journey === JOURNEYS.FAI ? clearedPlan : { ...clearedFai, ...clearedPlan };
};

export const cartStore = create<cartStoreType>((set) => ({
    selectedPlan: null,
    setSelectedPlan: (product: PlanType | null) =>
        set(() => ({
            selectedPlan: product,
        })),
    selectedOption: null,
    setSelectedOption: (option) =>
        set(() => ({
            selectedOption: option,
        })),
    selectedFaiProduct: null,
    setSelectedFaiProduct: (faiProduct) =>
        set(() => ({
            selectedFaiProduct: faiProduct,
        })),
    byouCart: JSON.parse(getLocalDataIfNotExpired(CART_STORAGE_KEY) || '{}'),
    addToCart: ({ product, options }) =>
        set(() => {
            const expirationDate = new Date();
            expirationDate.setHours(expirationDate.getHours() + 6);
            const tCart: CartType = {
                gencode: product.gencode,
                product: { ...product },
                total: 0,
                itemsCount: 1,
                options,
                tempId: uuid(),
                expiresTimestamp: expirationDate.getTime(),
            };
            tCart.total = calculateTotal(tCart);

            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(tCart));

            return {
                byouCart: tCart,
            };
        }),
    addDeliveryMode: (deliveryMode) => {
        set((state) => {
            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                    deliveryMode,
                },
            };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    removeDeliveryMode: () => {
        set((state) => {
            if (state.byouCart?.deliveryMode) {
                delete state.byouCart?.deliveryMode;
            }

            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                },
            };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    updateCart: (cartItems) =>
        set((state) => {
            const updatedCart = { ...(state.byouCart as CartType), ...cartItems };
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart)); // Save to local storage
            return { byouCart: updatedCart };
        }),
    updateCartOptions: (option) => {
        set((state) => {
            const options = state.byouCart?.options ?? [];
            const findOptionIndex = options.findIndex((o) => o.gencode === option.gencode);

            const findExistingParentalOption = options.findIndex((o) =>
                o.categories?.includes('controleparental-byou'),
            );
            if (option.categories?.includes('controleparental-byou') && findExistingParentalOption !== -1) {
                options.splice(findExistingParentalOption, 1);
            }

            if (findOptionIndex >= 0) {
                options.splice(findOptionIndex, 1);
            } else {
                options.push(option);
            }
            const updatedCart = {
                byouCart: {
                    ...(state.byouCart as CartType),
                    options,
                },
            };
            updatedCart.byouCart.total = calculateTotal(updatedCart.byouCart);
            setLocalVariable(CART_STORAGE_KEY, JSON.stringify(updatedCart.byouCart));

            return updatedCart;
        });
    },
    selectedFaiOption: null,
    setSelectedFaiOption: (optionId) => {
        set(() => ({
            selectedFaiOption: optionId,
        }));
    },
    temporarilySelectedPlan: null,
    setTemporarilySelectedPlan: (plan) => {
        set(() => ({
            temporarilySelectedPlan: plan,
        }));
    },
    selectedNetwork: Connectivity.FOUR_G,
    setSelectedNetwork: (network) => {
        set(() => ({
            selectedNetwork: network,
        }));
    },
    temporarilySelectedFaiProduct: null,
    setTemporarilySelectedFaiProduct: (temporaryFaiProduct) => {
        set(() => ({
            temporarilySelectedFaiProduct: temporaryFaiProduct,
        }));
    },
    otisConfigurator: { value: undefined },
    setOtisConfigurator: (newJourney) => {
        set(() => ({
            otisConfigurator: { value: newJourney },
        }));
    },
    switchJourney: (journey) =>
        set((state) => {
            if (state.otisConfigurator?.value === journey) return state;

            return {
                ...state,
                otisConfigurator: { value: journey },
                ...getJourneyResets(journey),
            };
        }),
    consent: JSON.parse(getLocalVariable(CONSENT_KEY) || '{}'),
    setConsent: (consentData) =>
        set((state) => {
            const updatedConcent = { ...state.consent, ...consentData };
            setLocalVariable(CONSENT_KEY, JSON.stringify(updatedConcent));
            return { consent: updatedConcent };
        }),
}));

const calculateTotal = (cart: CartType): number => {
    if (!cart.product) return 0;

    return cart.options.reduce((acc, o) => acc + (o.prices?.final ?? 0), cart.product.prices.final);
};
